import { useMemo, useState } from "react";
import { useCustom, useTranslate } from "@refinedev/core";
import { Button, PawIcon, StatusBadge } from "@fawkes/ui";

// De werklijst "kenmerken invullen" (epic 12, story 12-9).
//
// Waarom dit scherm bestaat: de zestien vragen van de kenmerkencatalogus worden bij het aanmaken van
// een advertentie als lege rijen aangelegd en daarna zelden ingevuld. De publieke matchwizard leest
// precies die antwoorden — zonder vulling antwoordt hij op elke vraag "onbekend" en is hij nutteloos.
//
// Het vult hier zelf niets in. Dat kan al, in de editor, mét de AI-knop en mét de zin uit het dossier
// ernaast; dat scherm nabouwen zou twee plekken opleveren die uit elkaar lopen. Wat ontbrak is het
// overzicht: wáár de gaten zitten, hoe groot het werk is, en bij welk dier de AI kans van slagen
// heeft. Eén klik verder zit je in de editor die het al kan.

interface Rij {
  id: string;
  dierId: number | null;
  naam: string | null;
  soort: string | null;
  ras: string | null;
  status: string;
  open: number;
  totaal: number;
  /** Of er genoeg gedragstekst in het dossier staat om de AI iets te laten voorstellen. */
  heeftDossier: boolean;
}

interface Werklijst {
  rijen: Rij[];
  totaal: number;
  openTotaal: number;
}

const PAGINA = 25;

export function KenmerkWerklijst({ onOpen }: { onOpen: (rij: Rij) => void }) {
  const t = useTranslate();
  const [size, setSize] = useState(PAGINA);
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useCustom<Werklijst>({
    url: "herplaatsing/kenmerken-werklijst",
    method: "get",
    config: { query: { page: 0, size } },
  });

  const lijst = data?.data;
  // Alleen wat er nog toe doet: een advertentie waarvan alles beantwoord is hoort niet in een
  // werklijst. De telling aan de serverkant blijft over álle advertenties gaan, zodat de kop de
  // omvang van het werk noemt en niet de omvang van deze pagina.
  const teDoen = useMemo(() => (lijst?.rijen ?? []).filter((r) => r.open > 0), [lijst]);
  const metDossier = useMemo(() => teDoen.filter((r) => r.heeftDossier).length, [teDoen]);

  if (!isLoading && lijst && lijst.openTotaal === 0) {
    return null;
  }

  return (
    <section className="fawkes-kenmerkwerk">
      <header className="fawkes-kenmerkwerk__kop">
        <span className="fawkes-kenmerkwerk__icoon" aria-hidden="true"><PawIcon /></span>
        <div>
          <h2>{t("kenmerkWerklijst.title")}</h2>
          <p>
            {lijst
              ? t("kenmerkWerklijst.subtitle")
                  .replace("%open%", String(lijst.openTotaal))
                  .replace("%dieren%", String(lijst.totaal))
              : t("kenmerkWerklijst.laden")}
          </p>
        </div>
        <Button variant="secondary" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          {open ? t("kenmerkWerklijst.verberg") : t("kenmerkWerklijst.toon")}
        </Button>
      </header>

      {open && (
        <>
          <p className="fawkes-kenmerkwerk__uitleg">
            {t("kenmerkWerklijst.uitleg")}
            {metDossier > 0 && " " + t("kenmerkWerklijst.uitlegAi").replace("%n%", String(metDossier))}
          </p>

          <ul className="fawkes-kenmerkwerk__lijst">
            {teDoen.map((rij) => (
              <li key={rij.id}>
                <div className="fawkes-kenmerkwerk__dier">
                  <strong>{rij.naam ?? t("kenmerkWerklijst.naamloos")}</strong>
                  <small>{[rij.soort, rij.ras].filter(Boolean).join(" · ")}</small>
                </div>
                <span className="fawkes-kenmerkwerk__open">
                  {t("kenmerkWerklijst.openVan")
                    .replace("%open%", String(rij.open))
                    .replace("%totaal%", String(rij.totaal))}
                </span>
                {rij.heeftDossier
                  ? <StatusBadge tone="info">{t("kenmerkWerklijst.aiKanHelpen")}</StatusBadge>
                  : <span className="fawkes-kenmerkwerk__geenbron">{t("kenmerkWerklijst.geenDossier")}</span>}
                <Button variant="secondary" onClick={() => onOpen(rij)}>
                  {t("kenmerkWerklijst.invullen")}
                </Button>
              </li>
            ))}
          </ul>

          {teDoen.length === 0 && !isLoading && (
            <p className="fawkes-kenmerkwerk__leeg">{t("kenmerkWerklijst.allesGedaan")}</p>
          )}

          {lijst && lijst.rijen.length < lijst.totaal && (
            <Button variant="secondary" onClick={() => setSize((s) => s + PAGINA)} disabled={isLoading}>
              {t("kenmerkWerklijst.meer")}
            </Button>
          )}
        </>
      )}
    </section>
  );
}
